// File: /opt/stacks/workout/app/src/views/CoachView.js
import React, { useContext } from "react";
import {
  AppStateContext,
  TimerContext,
  WorkoutContext,
} from "../contexts/WorkoutContext";
import WorkoutCoachView from "../components/workout/WorkoutCoachView";
import CoachAtRest from "../components/workout/CoachAtRest";
import useWakeLock from "../hooks/useWakeLock";
import useMediaSession, { unlockMediaAudio } from "../hooks/useMediaSession";
import { ensureAudioContext } from "../hooks/useSound";
import useImmersiveCoach from "../hooks/useImmersiveCoach";
import useWorkoutSettings from "../hooks/useWorkoutSettings";
import { getAudioSettings } from "../hooks/useAudioSettings";
import useWorkoutUiState, { formatSeconds } from "../hooks/useWorkoutUiState";
import useLocalStorage from "../hooks/useLocalStorage";
import { useLiveSession } from "../contexts/SocialContext";
import { Button } from "../components/ui/button";

import {
  XCircle,
  Play,
  Clock,
  Dumbbell,
  Radio,
  Cpu,
  Loader2,
  Bike,
  Users,
  Globe,
  Lock,
  Tv,
  AlarmClock,
} from "lucide-react";
import { treeHasBike } from "../utils/workoutPlanUtils";
import { formatDuration } from "../utils/time";
import { buildLiveTimeline, findTimelineIndexFor } from "../utils/liveTimeline";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import { useLiveBroadcast } from "../hooks/useLiveBroadcast";
import useTotalDuration from "../hooks/useTotalDuration";
import useExerciseHistory from "../hooks/useExerciseHistory";

import SessionCompleteModal from "../components/workout/SessionCompleteModal";
import { sessionMachineEnergy } from "../utils/telemetry/energy";
import GoLiveSheet from "../components/workout/GoLiveSheet";
import { Group, ItemRow } from "../components/profile/ProfileParts";
import LiveSessionFlow from "../components/workout/LiveSessionFlow";
import useDeviceHr from "../hooks/useDeviceHr";
import useLiveHr from "../hooks/useLiveHr";
import useDeviceCast from "../hooks/useDeviceCast";
import { useConnectedEquipment } from "../contexts/ConnectedEquipmentContext";
import { machinesUsed } from "../utils/ble/deviceInfo";
import { equipmentIcon } from "../components/settings/BluetoothSheet";
import { equipmentTypeForNode } from "../utils/workoutPlanUtils";
import SwipeablePanes from "../components/workout/SwipeablePanes";
import EquipmentDashboard from "../components/workout/dashboards/EquipmentDashboard";
import EquipmentSidePanel from "../components/workout/dashboards/EquipmentSidePanel";
import usePhoneLandscape from "../hooks/usePhoneLandscape";
import { cn } from "../lib/utils";
import QuickSwitch from "../components/workout/QuickSwitch";
import DeviceLiveView from "../components/settings/DeviceLiveView";
import SessionPlanOutline from "../components/workout/SessionPlanOutline";
import FirstVisitTip from "../components/help/FirstVisitTip";
import RepsAdjustSheet from "../components/workout/RepsAdjustSheet";
import { heartRateBasis, zoneOf } from "../utils/heartRateZones";
import { addHrSample, emptyHrStats, isFreeTraining } from "../utils/freeTraining";
import { freeBlockAt } from "../utils/keepGoing";
import {
  buildExerciseList,
  checklistSessions,
  endRest,
  openRest,
  setRestTarget,
  startRest,
} from "../utils/freeTrainingLog";
import ChecklistSheet, { ChecklistGroup } from "../components/workout/FreeTrainingChecklist";
import LoadAdjustDialog from "../components/workout/LoadAdjustDialog";
import SessionEditDialog from "../components/workout/SessionEditDialog";
import {
  FinishBySheet,
  TrimSuggestionSheet,
} from "../components/workout/FinishBySheets";
import {
  useFinishByDeadline,
  useFinishByMonitor,
} from "../hooks/useFinishBy";
import { formatClock } from "../utils/finishBy";
import { ColdWarmupSheet } from "../components/workout/ColdWarmupSheet";
import { useColdWarmupOffer } from "../hooks/useColdWarmup";
import { useToast } from "../contexts/ToastContext";

// Walk the plan tree and return an array of leaf exercises
const flattenExercises = (node) => {
  if (!node) return [];
  if (!node.children || node.children.length === 0) {
    return [
      {
        label: node.label,
        value: node.value,
        unit: node.unit,
        type: node.type,
      },
    ];
  }
  return node.children.flatMap((child) => flattenExercises(child));
};

// A row icon that spins while something is in progress.
const SpinningLoader = ({ className }) => (
  <Loader2 className={`${className || ""} animate-spin`} />
);

// --- State A: A guided session is loaded ---
const GuidedSessionView = ({
  appState,
  workoutPlan,
  onStartWorkout,
  onFinishWorkout,
  onClearSession,
  deviceCast,
}) => {
  const { coachTick } = useContext(TimerContext);
  const { playPause, coachState, keepGoing } = useContext(AppStateContext);
  const { user } = useAuth();
  const { showToast } = useToast();
  const totalSeconds = useTotalDuration(workoutPlan);
  const [workoutSettings] = useWorkoutSettings();

  // Finish-by deadline (#8), set on the start screen; the active coach watches
  // it. Cleared once the session is over so it can't carry into the next one.
  const { deadline, setDeadline } = useFinishByDeadline(workoutPlan);
  // A phone on its side gets the start screen in two columns (#227):
  // Start stays in view on the left, everything else scrolls on the right.
  const phoneLandscape = usePhoneLandscape();
  const [finishByOpen, setFinishByOpen] = React.useState(false);
  React.useEffect(() => {
    if (appState === "finished" && deadline) setDeadline(null);
  }, [appState, deadline, setDeadline]);

  // FTMS-connected equipment (Web Bluetooth) lives in a top-level provider so
  // a) the connection survives navigation between Settings and Coach, and
  // b) multiple devices can stay paired concurrently (e.g. bike + rower in a
  // circuit). `setWorkoutActive` toggles the per-device sample-accumulation
  // gate that drives session aggregates.
  const equipment = useConnectedEquipment();
  // Still on at the finish: turning it back on for Keep going (#199) would
  // start the bike's totals again.
  React.useEffect(() => {
    equipment.setWorkoutActive(appState === "active" || appState === "finished");
  }, [appState, equipment]);
  // Free training (#146) could be on anything, so it always offers a rower
  // or bike.
  const freeTraining = isFreeTraining(workoutPlan);
  // A Free training block kept going into (#199) offers them too.
  const keptGoing = !!workoutPlan?.children?.some((c) => c?.freeTraining === true);
  const sessionHasEquipment = freeTraining || keptGoing || treeHasBike(workoutPlan);
  const showConnectTrainer = workoutSettings.showConnectTrainer ?? true;
  const bluetoothSupported = equipment.isSupported;

  // The telemetry recorder lives in App (CoachTelemetry), so it keeps
  // recording while you look at another tab.
  const {
    setWorkoutPlan,
    getDoneRepsSets,
    amendSetReps,
    // The full state, for free training's log (#157); AppStateContext's copy
    // leaves it out.
    coachState: fullCoachState,
    updateFreeLog,
    updateFreeChecklist,
    telemetryPeekRef,
  } = useContext(WorkoutContext);
  const { liveSession, createLive, loadLive } = useLiveSession();
  const { profile, sessions, performanceCapabilities } = useUserProfile();
  // Free training's checklist (#164): jotted here before Start, from your
  // exercise list or a saved session; what's not logged shows at the end.
  const exerciseList = React.useMemo(
    () => (freeTraining ? buildExerciseList(sessions, performanceCapabilities) : []),
    [freeTraining, sessions, performanceCapabilities],
  );
  const checklistFrom = React.useMemo(
    () => (freeTraining ? checklistSessions(sessions) : []),
    [freeTraining, sessions],
  );
  const [checklistSheet, setChecklistSheet] = React.useState(null);
  const historyMap = useExerciseHistory();
  const { isLive, isGoingLive, goLive } = useLiveBroadcast(
    workoutPlan,
    workoutPlan?.label,
  );

  // Sticky rather than per-session state: whether you are happy being watched
  // is a standing preference about yourself, not a decision worth re-taking
  // every time you go live. Defaults off — a watch link must be opted into.
  const [allowWatchers, setAllowWatchers] = useLocalStorage(
    "live_allow_watchers",
    false,
  );

  // Media Session handlers are direction-aware, not the blind playPause
  // toggle: Bluetooth headsets / notification controls can deliver duplicate
  // or stale actions, and a toggle turns those into pause+resume back-to-back
  // (two overlapping cues, engine state flapping). A no-op in the already-
  // matching state is always safe.
  const coachRunning = coachState?.status === "running";
  const onMediaPlay = React.useCallback(() => {
    if (!coachRunning) playPause();
  }, [coachRunning, playPause]);
  const onMediaPause = React.useCallback(() => {
    if (coachRunning) playPause();
  }, [coachRunning, playPause]);

  useMediaSession({
    isActive: appState === "active",
    isPlaying: coachRunning,
    exerciseName: coachState?.currentNode?.label,
    workoutName: workoutPlan?.label,
    onPlay: onMediaPlay,
    onPause: onMediaPause,
  });

  // ─── Send to Device: open a solo session channel (v3) ───
  // Creates a kind:"solo" live session and stages the compiled timeline on
  // the server's runner. The user's hardware attaches automatically (the
  // server pushes session:state to linked devices) and this tab re-renders
  // through LiveSessionFlow as a follower — web and device share one clock.
  const [sendingToDevice, setSendingToDevice] = React.useState(false);
  const [goLiveOpen, setGoLiveOpen] = React.useState(false);
  // `target` names one timer: a mate's shared timer, or one of your own to
  // take back from a mate using it ({ device_id, take_over }). Without it the
  // session goes to every timer you own, skipping any a mate is using (#171).
  const sendToDevice = React.useCallback(async (target = {}) => {
    if (!workoutPlan || sendingToDevice) return;
    setSendingToDevice(true);
    try {
      // First in keeps a timer. Check now, not when the screen opened, so a
      // session isn't made for a timer someone else just started on.
      if (target.device_id != null && !target.take_over) {
        const fresh = await deviceCast.refreshDevices();
        const timer = [...fresh.devices, ...fresh.sharedDevices].find((d) => d.id === target.device_id);
        if (timer?.in_use_by) {
          showToast({
            title: `${timer.name} is in use`,
            description: `${timer.in_use_by} is training on it. Try again when they finish.`,
          });
          return;
        }
      }
      const timeline = buildLiveTimeline(workoutPlan, {
        profile,
        historyMap,
        skipLastRest: workoutSettings.skipLastRest ?? true,
      });
      await createLive({
        kind: "solo",
        title: workoutPlan?.label || "Workout",
        plan_data: workoutPlan,
      });
      await loadLive(
        timeline,
        {
          countInSeconds: getAudioSettings().countInDuration,
          changeoverHold: workoutSettings.changeoverHold,
        },
        target,
      );
    } catch (err) {
      console.error("[solo] send to device failed:", err);
    } finally {
      setSendingToDevice(false);
    }
  }, [
    workoutPlan,
    sendingToDevice,
    profile,
    historyMap,
    workoutSettings.skipLastRest,
    workoutSettings.changeoverHold,
    createLive,
    loadLive,
    deviceCast,
    showToast,
  ]);

  if (appState === "finished") {
    return (
      <SessionCompleteModal
        machineEnergy={sessionMachineEnergy(telemetryPeekRef?.current?.())}
        workoutPlan={workoutPlan}
        // Loads corrected here land in the plan before it is persisted, so the
        // saved session records what was lifted rather than what was planned.
        onPlanChange={setWorkoutPlan}
        elapsedSessionTime={coachTick.elapsedSessionTime}
        repsSets={getDoneRepsSets?.()}
        onAmendReps={amendSetReps}
        onSave={(feedback) => onFinishWorkout(feedback)}
        onKeepGoing={keepGoing}
        onSkip={onClearSession}
        saveLabel="Save to history"
        skipLabel="Discard"
        freeLog={fullCoachState?.freeLog || []}
        freeChecklist={fullCoachState?.freeChecklist || null}
        exerciseList={exerciseList}
        onFreeLogChange={updateFreeLog}
      />
    );
  }

  if (appState === "ready") {
    const exercises = flattenExercises(workoutPlan);
    const duration = formatDuration(totalSeconds);
    const endTime =
      totalSeconds > 0
        ? new Date(Date.now() + totalSeconds * 1000).toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          })
        : null;

    // Each band is gated on having something in it: the section is a
    // flex-col with gap-6, so an empty row is a visible hole rather than
    // nothing.
    const showEquipmentAction =
      showConnectTrainer &&
      bluetoothSupported &&
      sessionHasEquipment &&
      !equipment.active;
    const showGoLiveAction = !!user && !isLive;
    const sharedTimers = user ? deviceCast.sharedDevices || [] : [];
    const showDeviceAction = !!user && (deviceCast.devices.length > 0 || sharedTimers.length > 0);
    const ownTimer =
      deviceCast.devices.find((d) => d.id === deviceCast.selectedDeviceId) ||
      deviceCast.devices[0] ||
      null;
    const deviceName = ownTimer?.name || "Khronos Timer";
    // Your own timer with a mate's session on it: tapping takes it back.
    const ownTimerTaken = deviceCast.devices.length === 1 && !!ownTimer?.in_use_by;
    // Remembered gear Chrome can't hand back on its own (#169) gets a row
    // each: the picker needs a tap per device, filtered to that one. The
    // strap matters to any session; a bike or rower only to one that uses it.
    const pickerGear = showConnectTrainer && bluetoothSupported
      ? [
          ...(showEquipmentAction
            ? (equipment.rememberedDevices || []).filter((d) => d.needsPicker)
            : []),
          ...(equipment.rememberedHr?.needsPicker
            ? [{ ...equipment.rememberedHr, type: "hr_monitor" }]
            : []),
        ]
      : [];
    const pickerHasMachine = pickerGear.some((d) => d.type !== "hr_monitor");
    const canReconnectEquipment = !!(
      equipment.rememberedDevices?.some((d) => !d.needsPicker) ||
      (equipment.rememberedHr && !equipment.rememberedHr.needsPicker)
    );
    const exerciseCount = exercises.filter((e) => {
      const t = (e.type || "").toLowerCase();
      return t !== "rest" && t !== "setrest";
    }).length;

    const twoCol = phoneLandscape;
    const clearEl = (
      <div className="flex justify-center w-full">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSession}
          className="text-muted-foreground"
        >
          <XCircle className="mr-2 h-4 w-4" />
          Clear session
        </Button>
      </div>
    );
    const column = "flex w-full flex-col items-center gap-6";
    return (
      <div
        className={
          twoCol
            ? "mx-auto grid max-w-4xl grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start gap-6 px-4 py-3"
            : "max-w-lg mx-auto flex flex-col items-center gap-6 p-4"
        }
      >
        <div className={twoCol ? "sticky top-3 flex w-full flex-col items-center gap-2" : "contents"}>
        <div className="text-center w-full">
          <h2 className="text-2xl font-bold mb-1">
            {workoutPlan?.label || "Your session"}
          </h2>
          {workoutPlan?.description && (
            <p className="text-sm text-muted-foreground mb-2">
              {workoutPlan.description}
            </p>
          )}
          {freeTraining ? (
            <p className="text-sm text-muted-foreground">
              Connect anything you&apos;re using, then start the clock and log
              what you do as you go.
            </p>
          ) : (
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              {duration && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {duration}
                </span>
              )}
              {endTime && <span>Ends ~{endTime}</span>}
              <span className="flex items-center gap-1">
                <Dumbbell className="h-3.5 w-3.5" />
                {exerciseCount} exercises
              </span>
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFinishByOpen(true)}
            className={
              deadline
                ? "mt-2 gap-1.5 text-primary hover:text-primary"
                : "mt-2 gap-1.5 text-muted-foreground"
            }
          >
            <AlarmClock className="h-4 w-4" />
            {deadline ? `Finish by ${formatClock(deadline.at)}` : "Set a finish-by time"}
          </Button>
          <FinishBySheet
            open={finishByOpen}
            onOpenChange={setFinishByOpen}
            deadline={deadline}
            setDeadline={setDeadline}
            plannedSec={totalSeconds}
          />
        </div>

        <button
          onClick={() => {
            ensureAudioContext();
            unlockMediaAudio();
            onStartWorkout();
          }}
          aria-label="Start workout"
          className={cn(
            "relative flex items-center justify-center",
            twoCol ? "w-[150px] h-[150px]" : "w-[200px] h-[200px]"
          )}
        >
          <svg
            width={twoCol ? 150 : 200}
            height={twoCol ? 150 : 200}
            viewBox="0 0 200 200"
            className="absolute inset-0"
          >
            <circle
              cx={100}
              cy={100}
              r={90}
              fill="none"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth={10}
            />
          </svg>
          <div
            className={cn(
              "flex items-center justify-center rounded-full bg-primary/20 hover:bg-primary/30 transition-colors",
              twoCol ? "w-[105px] h-[105px]" : "w-[140px] h-[140px]"
            )}
          >
            <Play className={cn("text-primary fill-current", twoCol ? "h-12 w-12 ml-1.5" : "h-16 w-16 ml-2")} />
          </div>
        </button>
        </div>

        <div className={twoCol ? column : "contents"}>

        {/* The first time anyone reaches the start screen: one line on the
            two things the Coach does differently, so the outline stays in
            view. The rest is a tap away in Help. */}
        <div className="w-full">
          {/* Free training has no reps steps: its tip is about the log (#157). */}
          {freeTraining ? (
            <FirstVisitTip id="free-training" title="Free training" topic="timers#free-training" line>
              Log each set in the box at the bottom, numbers and all: &ldquo;bench 80x8&rdquo;.
            </FirstVisitTip>
          ) : (
            <FirstVisitTip id="coach" title="Before you start" topic="coach-basics" line>
              Tap the ring to pause. On a reps step, press Done.
            </FirstVisitTip>
          )}
        </div>

        {/* Free training has one step, the clock: nothing to outline. It
            can have a checklist instead (#164). */}
        {freeTraining && (
          <div className="w-full">
            <ChecklistGroup
              checklist={fullCoachState?.freeChecklist || null}
              onChange={updateFreeChecklist}
              onOpen={() => setChecklistSheet(true)}
            />
            {checklistSheet && (
              <ChecklistSheet
                checklist={fullCoachState?.freeChecklist || null}
                onChange={updateFreeChecklist}
                list={exerciseList}
                sessions={checklistFrom}
                onClose={() => setChecklistSheet(null)}
              />
            )}
          </div>
        )}

        {!freeTraining && (
          <SessionPlanOutline
            plan={workoutPlan}
            // Side by side, the page scrolls the column: a nested scroller
            // in a 372 px tall screen would show three rows.
            maxHeight={twoCol ? "" : "max-h-[45vh]"}
            className="w-full"
          />
        )}

        {/* The ring is the only primary action (#110). Everything else is a
            row: pairing is its own question, and Go live and Send to a Khronos
            Timer are the same question (where does this session run), so
            they share a group. Clear session is the one destructive action
            and sits apart at the bottom. */}
        {(showEquipmentAction || pickerGear.length > 0) && (
          <div className="w-full">
            <Group title="Equipment">
              {pickerGear.map((d) => {
                const isHr = d.type === "hr_monitor";
                const busy = isHr ? equipment.hrPairing : equipment.pairing;
                const pair = isHr ? equipment.addHrMonitor : equipment.addDevice;
                return (
                  <ItemRow
                    key={isHr ? "hr" : d.id}
                    Icon={busy ? SpinningLoader : equipmentIcon(d.type)}
                    title={`Connect ${d.name}`}
                    detail={busy ? "Pairing..." : "Switch it on, then pick it in the list"}
                    disabled={busy}
                    onClick={() => pair({ name: d.advertisedName || d.name })}
                  />
                );
              })}
              {showEquipmentAction && !pickerHasMachine && (
                <ItemRow
                  Icon={equipment.pairing ? SpinningLoader : Bike}
                  title={canReconnectEquipment ? "Reconnect equipment" : "Connect equipment"}
                  detail={equipment.pairing ? "Pairing..." : "Bike, rower or heart-rate strap"}
                  disabled={equipment.pairing}
                  onClick={
                    canReconnectEquipment
                      ? equipment.reconnectPersisted
                      : equipment.addDevice
                  }
                />
              )}
            </Group>
          </div>
        )}

        {(showGoLiveAction || showDeviceAction) && (
          <div className="w-full">
            <Group title="Share this session">
              {showGoLiveAction && (
                <ItemRow
                  Icon={Radio}
                  title="Go live"
                  detail={
                    isGoingLive ? "Starting..." : "Train with mates, or let people watch"
                  }
                  disabled={isGoingLive}
                  onClick={() => setGoLiveOpen(true)}
                />
              )}
              {showDeviceAction && ownTimer && (
                <ItemRow
                  Icon={sendingToDevice ? SpinningLoader : Cpu}
                  title="Send to a Khronos Timer"
                  detail={
                    sendingToDevice
                      ? "Sending..."
                      : ownTimerTaken
                        ? `${ownTimer.in_use_by} is using ${deviceName}. Tap to take it back`
                        : `${deviceName}${deviceCast.isOnline ? "" : " is offline"}`
                  }
                  disabled={sendingToDevice || !deviceCast.isOnline}
                  onClick={() =>
                    sendToDevice(ownTimerTaken ? { device_id: ownTimer.id, take_over: true } : {})
                  }
                />
              )}
              {showDeviceAction &&
                sharedTimers.map((timer) => (
                  <ItemRow
                    key={`shared-${timer.id}`}
                    Icon={sendingToDevice ? SpinningLoader : Cpu}
                    title={`Send to ${timer.name}`}
                    detail={
                      sendingToDevice
                        ? "Sending..."
                        : timer.in_use_by
                          ? `In use by ${timer.in_use_by}`
                          : `${timer.owner_name}'s timer`
                    }
                    disabled={sendingToDevice || !!timer.in_use_by}
                    onClick={() => sendToDevice({ device_id: timer.id })}
                  />
                ))}
            </Group>
          </div>
        )}

        {twoCol && clearEl}
        </div>

        {!twoCol && clearEl}

        <GoLiveSheet
          open={goLiveOpen}
          onOpenChange={setGoLiveOpen}
          goLive={goLive}
          allowWatchers={allowWatchers}
          setAllowWatchers={setAllowWatchers}
        />
      </div>
    );
  }

  // Active workout — adapt guided coachState to unified props
  return (
    <GuidedActiveAdapter
      equipment={equipment}
      liveSession={liveSession}
    />
  );
};

// --- Adapter: Maps guided coachState → WorkoutCoachView props ---
const GuidedActiveAdapter = ({ equipment, liveSession }) => {
  const {
    coachState,
    workoutPlan,
    playPause,
    skipToNext,
    skipToPrevious,
    confirmReady,
    skipRoundForward,
    skipRoundBack,
    completeWorkout,
    startWorkout,
    savePartialSession,
    clearLoadedSession,
    hrSummaryRef,
    bikeSummaryRef,
    machinesRef,
    phaseLogRef,
    countInTotal,
    getActiveLoadContext,
    updateActiveLoad,
    getLastRepsSet,
    amendSetReps,
    updateFreeLog,
    updateFreeChecklist,
    updateFreeRests,
    applySessionEdit,
    getTrimContext,
  } = useContext(WorkoutContext);
  const { countInRemaining } = useContext(TimerContext);
  const { showToast } = useToast();
  const { isImmersive, setImmersive } = useContext(AppStateContext);
  const { profile, sessions, performanceCapabilities } = useUserProfile();
  const historyMap = useExerciseHistory();
  const [workoutSettingsForLive] = useWorkoutSettings();
  const { isGoingLive, goLiveFromHere } = useLiveBroadcast(
    workoutPlan,
    workoutPlan?.label,
  );

  // Go live from here (#81): the session is already running, so the timeline
  // is the whole plan and the runner is told where to pick it up. Both sides
  // compile through compileWorkoutPlan, but buildLiveTimeline drops the
  // non-executable transitions the engine keeps — so the position travels as
  // a path + loopCounters identity rather than as an index.
  const [goLiveOpen, setGoLiveOpen] = React.useState(false);
  const goLiveFromCurrentPosition = React.useCallback(
    (privacy, opts = {}) => {
      if (!workoutPlan) return;
      const timeline = buildLiveTimeline(workoutPlan, {
        profile,
        historyMap,
        skipLastRest: workoutSettingsForLive.skipLastRest ?? true,
      });
      const atIndex = findTimelineIndexFor(timeline, {
        path: coachState?.path,
        loopCounters: coachState?.loopCounters,
      });
      setGoLiveOpen(false);
      // Fire-and-forget is safe: goLiveFromHere reports its own failures, and
      // the workout keeps running either way.
      goLiveFromHere(privacy, {
        ...opts,
        timeline,
        atIndex,
        priorPhaseLog: phaseLogRef?.current || null,
        changeoverHold: workoutSettingsForLive.changeoverHold,
        // The live session owns the clock and the record from here. Without
        // this the local engine keeps running underneath it: useWorkoutCoach
        // lives in AppStateContext, above the early return that swaps in
        // LiveSessionFlow, so nothing else would ever stop it.
        onHandover: () => clearLoadedSession?.(),
      });
    },
    [
      workoutPlan,
      profile,
      historyMap,
      workoutSettingsForLive.skipLastRest,
      coachState?.path,
      coachState?.loopCounters,
      goLiveFromHere,
      phaseLogRef,
      clearLoadedSession,
    ],
  );

  // Mid-session load editor (issue #76). The context is snapshotted when the
  // dialog opens rather than tracked live: the phase can auto-advance while it
  // is open, and an editor that silently re-aimed at the next set would write
  // the user's figure onto the wrong exercise.
  const [loadEdit, setLoadEdit] = React.useState(null);
  const openLoadEditor = React.useCallback(() => {
    const context = getActiveLoadContext?.();
    if (context) setLoadEdit(context);
  }, [getActiveLoadContext]);

  // Reps actually done (#97): the set just finished, offered for correction
  // during the rest after it. Snapshotted when the sheet opens so a write
  // can't land on a later set if another one is logged meanwhile.
  const lastRepsSet = getLastRepsSet?.() || null;
  const [repsEdit, setRepsEdit] = React.useState(null);

  // Mid-session session editor (#77). Offered only while PAUSED: the phase
  // countdown lives in React state, so rebuilding the queue under a running
  // clock would move the goalposts mid-phase. Pausing first is also the honest
  // gesture — you are not training while you rewrite the session.
  const [sessionEditOpen, setSessionEditOpen] = React.useState(false);
  // Where the session stands, so the editor can lock the steps already done.
  const sessionEditPosition = React.useMemo(
    () =>
      coachState?.path?.length
        ? { path: coachState.path, loopCounters: coachState.loopCounters || {} }
        : null,
    [coachState?.path, coachState?.loopCounters],
  );

  // Imperative handle on the Coach/Equipment pager so the workout phase can
  // nudge the visible pane (see auto-follow effect below).
  const panesRef = React.useRef(null);

  // NOTE: this adapter is now strictly LOCAL (offline solo) — device-synced
  // sessions run through the solo session channel and render via
  // LiveSessionFlow, so there is no cast mirroring or echo handling here.

  // Poll linked ESP32 device for HR data during active workout, with zones
  // from the Profile's heart rates (#94).
  const { userProfile: deviceZoneProfile } = useContext(WorkoutContext);
  const deviceHrBasis = React.useMemo(() => heartRateBasis(deviceZoneProfile), [deviceZoneProfile]);
  const isWorkoutActive =
    coachState.status === "running" ||
    coachState.status === "paused" ||
    coachState.status === "readyGate";
  const { currentBpm, hrSummaryRef: deviceHrRef } =
    useDeviceHr(isWorkoutActive, deviceHrBasis);
  // Unified live HR: direct BLE strap (primary) with the ESP32 poll as
  // fallback. One value drives the coach footer, the centre toggle, and the
  // ERG HR tile.
  const { bpm: liveBpm } = useLiveHr(currentBpm);

  // Free training (#146): running heart-rate figures for the stats strip,
  // one sample a second while the clock runs. Display only — the saved
  // session takes its heart rate from the telemetry recorder as usual.
  // A Free training block kept going into after the finish (#199) gets the
  // same screen, on a clock that starts with the block.
  const freeTraining =
    isFreeTraining(workoutPlan) || !!freeBlockAt(workoutPlan, coachState.path);
  const [freeHr, setFreeHr] = React.useState(emptyHrStats);
  const liveBpmRef = React.useRef(liveBpm);
  liveBpmRef.current = liveBpm;
  React.useEffect(() => setFreeHr(emptyHrStats()), [workoutPlan]);
  // Your exercise list for the log's suggestions (#157), from your history.
  const exerciseList = React.useMemo(
    () => (freeTraining ? buildExerciseList(sessions, performanceCapabilities) : []),
    [freeTraining, sessions, performanceCapabilities],
  );
  const checklistFrom = React.useMemo(
    () => (freeTraining ? checklistSessions(sessions) : []),
    [freeTraining, sessions],
  );
  React.useEffect(() => {
    if (!freeTraining || coachState.status !== "running") return undefined;
    const id = setInterval(() => {
      const bpm = liveBpmRef.current;
      const zone = bpm && deviceHrBasis ? zoneOf(bpm, deviceHrBasis) : null;
      setFreeHr((stats) => addHrSample(stats, bpm, zone));
    }, 1000);
    return () => clearInterval(id);
  }, [freeTraining, coachState.status, deviceHrBasis]);

  // Connected equipment context (Web Bluetooth) — provided at the app root
  // and threaded in via GuidedSessionView so the workout-active gate is set
  // exactly once for the whole session.
  const {
    devices,
    active: activeDevice,
    activeDeviceId,
    override,
    addDevice,
    setActive,
    clearOverride,
    reconnectPersisted,
    rememberedDevices,
    rememberedHr,
  } = equipment;
  const activeSummaryRef = activeDevice?.summaryRef;

  // Prefer a silent reconnect of a remembered device over opening the picker:
  // if we remember a device but have no live session, the user most likely
  // just needs to power it on, not re-pick it.
  // Gear only the picker can bring back (#169) goes straight to a picker
  // filtered to it, since a silent pass can't reach it.
  const hasRemembered = !!(
    rememberedDevices?.some((d) => !d.needsPicker) ||
    (rememberedHr && !rememberedHr.needsPicker)
  );
  const pickerMachine = rememberedDevices?.find((d) => d.needsPicker) || null;
  const connectOrReconnect = React.useCallback(() => {
    if (pickerMachine && devices.length === 0) {
      addDevice({ name: pickerMachine.advertisedName || pickerMachine.name });
    } else if (hasRemembered && devices.length === 0) {
      reconnectPersisted?.();
    } else {
      addDevice();
    }
  }, [pickerMachine, hasRemembered, devices.length, reconnectPersisted, addDevice]);

  // Immersive Coach: hides chrome, requests fullscreen, locks portrait while
  // the workout is running or paused. Auto-entry is mobile-only — desktop
  // users (and mobile users who exited mid-session) re-enter via the Maximize2
  // button rendered by WorkoutCoachView.
  const { enter: enterImmersive, exit: exitImmersive } = useImmersiveCoach({
    active: isWorkoutActive,
    setImmersive,
  });

  // Keep WorkoutContext's hrSummaryRef + bikeSummaryRef in sync. The legacy
  // bikeSummaryRef is preserved so the session-summary record keeps its
  // existing shape when only one bike was used.
  React.useEffect(() => {
    if (hrSummaryRef && deviceHrRef.current) {
      hrSummaryRef.current = deviceHrRef.current;
    }
    if (bikeSummaryRef && activeSummaryRef?.current) {
      bikeSummaryRef.current = activeSummaryRef.current;
    }
    // Every rower and bike that recorded this workout, for the save.
    const machines = machinesUsed(equipment.devices);
    if (machinesRef && machines) machinesRef.current = machines;
  });

  // ─── Auto-switch active stream by plan phase, with graceful fallback ───
  // When the current phase declares e.g. `equipment: "rower"` and a rower is
  // connected, swap the active stream to it. If the requested type is NOT
  // connected we DO NOT clear the active device — the user keeps streaming
  // from whatever they have (e.g. plan asks for rower, only the bike is
  // paired today). The user's manual override (recorded with a phaseId)
  // suppresses the auto-switch for the duration of that one phase.
  const currentPhaseId =
    coachState?.currentNode?.id ||
    (coachState?.path ? coachState.path.join(":") : null);
  React.useEffect(() => {
    if (!currentPhaseId) return;
    const wanted = equipmentTypeForNode(coachState?.currentNode);
    if (!wanted) return;
    if (override?.untilPhase === currentPhaseId) return;
    if (override && override.untilPhase !== currentPhaseId) {
      clearOverride();
    }
    const match = devices.find(
      (d) => d.type === wanted && d.status === "connected",
    );
    if (match && match.id !== activeDeviceId) {
      setActive(match.id, { auto: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhaseId]);

  const { status, currentNode, nextUpIsTimed } = coachState;

  // The state to render UI on both Host and Participant side
  const [workoutSettings, updateWorkoutSettings] = useWorkoutSettings();
  // Coach centre-display mode (Time / HR / Power / combos). Per-device glance
  // preference — kept in local storage rather than the synced workout settings.
  const [coachCenterMode, setCoachCenterMode] = useLocalStorage(
    "coachCenterMode",
    "time",
  );

  // ─── Auto-follow the visible pane to the phase's modality ───
  // When a phase wants equipment we have connected, slide to the equipment
  // pane; on coach/strength/rest phases slide back to Coach. A one-shot nudge
  // keyed on phase change (mirrors the active-stream auto-switch above) — the
  // user can still swipe freely between phases. Opt out via the setting.
  // Only nudge when the wanted pane differs from the last phase's: a rest
  // into a work set that both want Coach leaves a manual swipe alone (#148).
  const lastWantedPaneRef = React.useRef(null);
  React.useEffect(() => {
    if (!(workoutSettings.autoFollowPhasePane ?? true)) return;
    if (!currentPhaseId) return;
    if (coachState?.status === "readyGate") return;
    const hasEquipmentPane = equipment.isSupported && devices.length > 0;
    if (!hasEquipmentPane) return;
    const wanted = equipmentTypeForNode(currentNode);
    const match =
      !!wanted &&
      devices.some((d) => d.type === wanted && d.status === "connected");
    const wantedPane = match ? 1 : 0;
    if (wantedPane === lastWantedPaneRef.current) return;
    lastWantedPaneRef.current = wantedPane;
    panesRef.current?.goTo(wantedPane);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhaseId]);
  const uiState = useWorkoutUiState({
    coachState,
    workoutPlan,
    phaseLogRef,
    countInRemaining,
    countInTotal,
    profile,
    historyMap,
  });

  const isPaused = status === "paused";
  const isRunning = status === "running";

  // Finish-by deadline (#8): at a break — a rest, the Ready gate or paused,
  // never mid-set — suggest one trim when the session is heading past it.
  const currentType = String(currentNode?.type || "").toLowerCase();
  const finishBy = useFinishByMonitor({
    workoutPlan,
    remainingSec: uiState?.totalRemaining || 0,
    atBreak:
      isPaused ||
      status === "readyGate" ||
      currentType.includes("rest") ||
      currentType === "transition",
    active: isWorkoutActive,
    getTrimContext,
    applySessionEdit,
    showToast,
  });

  // Cold at the timer (#76): during the warm-up, offer a few more minutes.
  const coldWarmup = useColdWarmupOffer({
    workoutPlan,
    status,
    getTrimContext,
    applySessionEdit,
    showToast,
  });

  const phoneLandscape = usePhoneLandscape();

  if (!currentNode) {
    return (
      <div className="text-center text-muted-foreground">
        Loading workout...
      </div>
    );
  }

  // uiState calculations have been hoisted above and packed into `uiState`
  // We extract them here to render our own CoachView
  const {
    phaseType,
    phaseLabel,
    repDisplay,
    loadDisplay,
    progressItems,
    displayValue,
    displaySubValue,
    isRepBased,
    isCountUp,
    isSetupScreen,
    canGoBack,
    hasRoundSkip,
    nextUpLabel,
    nextUpValue,
    totalRemaining,
    endTimeStr,
    contextInfo,
    intensityTarget,
    checklistItems,
    blocks,
    activeGroupInfo,
    bikeRelevantPhase,
  } = uiState;

  // Show the secondary pane whenever any device is paired, so users can swipe
  // even while a device is reconnecting.
  const showEquipmentPane =
    equipment.isSupported && devices.length > 0;
  // A phone on its side (#149): the bike or rower sits beside the ring
  // rather than a swipe away (WorkoutCoachView's landscape layout).
  const equipmentBeside = showEquipmentPane && phoneLandscape && !freeTraining;

  // "Connect Equipment" footer prompt: only for equipment-relevant phases,
  // only while no device is currently active, gated by the Settings toggle.
  const showAddDevice =
    (workoutSettings.showConnectTrainer ?? true) &&
    equipment.isSupported &&
    !activeDevice &&
    // Free training could be on anything, so a rower or bike is always offered.
    (!!bikeRelevantPhase || freeTraining) &&
    (isPaused || devices.length > 0);

  // What pausing offers, on the Coach and on the bike or rower screen (#197).
  const pauseActions = isGoingLive
    ? [{ key: "golive", icon: Radio, label: "Going live..." }]
    : [
        {
          key: "golive",
          icon: Radio,
          label: "Go live",
          onClick: () => setGoLiveOpen(true),
        },
      ];
  const stopActions = [
    {
      label: "Save to history",
      withFeedback: true,
      onClick: (feedback) => savePartialSession(feedback),
    },
    {
      label: "Restart",
      onClick: startWorkout,
      variant: "restart",
    },
    {
      label: "Discard",
      onClick: clearLoadedSession,
      variant: "secondary",
    },
    { label: "Continue workout", cancel: true },
  ];
  const onEditSession =
    applySessionEdit && !freeTraining ? () => setSessionEditOpen(true) : undefined;

  const coachPane = (
    <div className="w-full max-w-lg lg:max-w-6xl phone-landscape:max-w-none mx-auto h-full">
      <WorkoutCoachView
        phaseType={phaseType}
        phaseLabel={phaseLabel}
        exerciseName={currentNode.label}
        exerciseDescription={currentNode.description}
        repDisplay={repDisplay}
        loadDisplay={loadDisplay}
        onEditLoad={openLoadEditor}
        lastRepsSet={lastRepsSet}
        onEditReps={amendSetReps ? () => setRepsEdit(lastRepsSet) : undefined}
        onEditSession={onEditSession}
        freeTraining={
          freeTraining
            ? {
                log: coachState.freeLog || [],
                list: exerciseList,
                onChange: updateFreeLog,
                checklist: coachState.freeChecklist || null,
                onChecklistChange: updateFreeChecklist,
                checklistSessions: checklistFrom,
                // Rest: tap to start (at the length last picked), tap again to
                // stop; logging stops it too.
                rests: coachState.freeRests || [],
                onRest: () =>
                  updateFreeRests((rests, now) =>
                    openRest(rests)
                      ? endRest(rests, now)
                      : startRest(rests, now, workoutSettings.freeRestTarget),
                  ),
                onRestTarget: (target) => {
                  updateWorkoutSettings({ freeRestTarget: target });
                  updateFreeRests((rests) => setRestTarget(rests, target));
                },
                elapsed: Math.max(
                  0,
                  (coachState.elapsedSessionTime || 0) - (coachState.freeStart || 0),
                ),
                hr: freeHr,
                zone: liveBpm && deviceHrBasis ? zoneOf(liveBpm, deviceHrBasis) : null,
                subscribe: activeDevice?.subscribe,
                hrSource: equipment.hr,
              }
            : null
        }
        intensityTarget={intensityTarget}
        checklistItems={checklistItems}
        countInRemaining={countInRemaining}
        countInTotal={countInTotal}
        isReadyGate={coachState.status === "readyGate"}
        readyGateElapsed={coachState.readyGateElapsed}
        faffTotal={coachState.faffTotal}
        onConfirmReady={confirmReady}
        progressItems={progressItems}
        // A block kept going into counts from its own start (#199), as its
        // dial and log do.
        displayValue={
          freeTraining && coachState.freeStart
            ? formatSeconds(Math.max(0, (coachState.elapsedSessionTime || 0) - coachState.freeStart))
            : displayValue
        }
        displaySubValue={displaySubValue}
        isRepBased={isRepBased}
        isSetupScreen={isSetupScreen}
        spinnerMode={isCountUp && isRunning}
        isPaused={isPaused}
        isRunning={isRunning}
        isFinished={false}
        isLast={coachState.isLast}
        onPlayPause={playPause}
        onSkipNext={skipToNext}
        onSkipBack={skipToPrevious}
        onFinish={completeWorkout}
        onRoundForward={skipRoundForward}
        onRoundBack={skipRoundBack}
        canGoBack={canGoBack}
        hasRoundSkip={hasRoundSkip}
        nextUpLabel={nextUpLabel}
        nextUpValue={nextUpValue}
        nextUpIsTimed={nextUpIsTimed}
        contextInfo={contextInfo}
        plan={workoutPlan}
        currentPath={coachState.path}
        loopCounters={coachState.loopCounters}
        phaseLog={phaseLogRef?.current}
        currentPhaseElapsedSec={Math.max(
          0,
          (coachState.elapsedSessionTime ?? 0) -
            (coachState.phaseStartElapsed ?? 0),
        )}
        totalRemaining={totalRemaining}
        endTime={endTimeStr}
        finishBy={
          finishBy.deadline
            ? {
                label: formatClock(finishBy.deadline.at),
                overSec: finishBy.overSec,
              }
            : null
        }
        collectStopFeedback
        pauseActions={pauseActions}
        stopActions={stopActions}
        currentBpm={liveBpm}
        hrZone={liveBpm && deviceHrBasis ? zoneOf(liveBpm, deviceHrBasis) : null}
        hrBasis={deviceHrBasis}
        sidePanel={
          equipmentBeside ? (
            <EquipmentSidePanel
              device={activeDevice}
              hr={equipment.hr}
              hrBpm={currentBpm}
              onAddDevice={connectOrReconnect}
            />
          ) : null
        }
        activeDevice={activeDevice}
        showAddDevice={showAddDevice}
        onAddDevice={connectOrReconnect}
        drainTimer={workoutSettings.drainTimer}
        drainSegments={workoutSettings.drainSegments}
        pauseOnMacroTap={workoutSettings.pauseOnMacroTap ?? true}
        centerMode={coachCenterMode}
        onSelectCenterMode={setCoachCenterMode}
        blocks={blocks}
        activeGroupInfo={activeGroupInfo}
        isImmersive={isImmersive}
        onEnterImmersive={enterImmersive}
        onExitImmersive={exitImmersive}
      />
      {/* Lives inside the coach pane so it renders on both return paths below
          (with and without the equipment pager). Radix portals it out anyway. */}
      <LoadAdjustDialog
        open={!!loadEdit}
        onOpenChange={(open) => {
          if (!open) setLoadEdit(null);
        }}
        context={loadEdit}
        onSubmit={updateActiveLoad}
      />
      <RepsAdjustSheet
        open={!!repsEdit}
        onOpenChange={(open) => {
          if (!open) setRepsEdit(null);
        }}
        set={repsEdit}
        onSubmit={amendSetReps}
      />
      <TrimSuggestionSheet
        offer={sessionEditOpen || loadEdit || repsEdit ? null : finishBy.offer}
        onTrim={finishBy.acceptTrim}
        onKeepGoing={finishBy.keepGoing}
      />
      <ColdWarmupSheet
        offer={
          sessionEditOpen || loadEdit || repsEdit || finishBy.offer
            ? null
            : coldWarmup.offer
        }
        onAdd={coldWarmup.accept}
        onDecline={coldWarmup.decline}
      />
      <SessionEditDialog
        open={sessionEditOpen}
        onOpenChange={setSessionEditOpen}
        plan={workoutPlan}
        position={sessionEditPosition}
        onSubmit={applySessionEdit}
      />
      {/* Same two questions as the Go live sheet (GoLiveSheet) — who may train
          with you, and whether anyone trains with you at all — asked again
          here because the session is already running (#81). The workout picks
          up from the block you are standing in, not from the warm-up. */}
      <AlertDialog open={goLiveOpen} onOpenChange={setGoLiveOpen}>
        <AlertDialogContent className="border-border/50 card-glow">
          <AlertDialogHeader>
            <AlertDialogTitle>Go live from here?</AlertDialogTitle>
            <AlertDialogDescription>
              Your session carries on from this block — nobody is sent back to
              the warm-up. Anyone who joins picks it up in progress.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Train together
            </p>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => goLiveFromCurrentPosition("friends")}
            >
              <Users className="mr-2 h-4 w-4" /> Mates
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => goLiveFromCurrentPosition("public")}
            >
              <Globe className="mr-2 h-4 w-4" /> Anyone
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => goLiveFromCurrentPosition("invite_only")}
            >
              <Lock className="mr-2 h-4 w-4" /> Invite only (join code)
            </Button>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mt-2">
              Broadcast
            </p>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() =>
                goLiveFromCurrentPosition("invite_only", {
                  mode: "broadcast",
                  allowWatchers: true,
                })
              }
            >
              <Tv className="mr-2 h-4 w-4" />
              <div className="flex flex-col items-start">
                <span>Watch only</span>
                <span className="text-xs text-muted-foreground">
                  Anyone with the link, no account needed
                </span>
              </div>
            </Button>
          </div>
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel>Keep it to myself</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (!showEquipmentPane || equipmentBeside) return coachPane;

  const equipmentPane = (
    <div className="w-full h-full flex flex-col">
      <QuickSwitch
        devices={devices}
        activeDeviceId={activeDeviceId}
        onSelect={setActive}
        currentPhaseId={currentPhaseId}
      />
      <div className="flex-1 min-h-0">
        <EquipmentDashboard
          device={activeDevice}
          onAddDevice={connectOrReconnect}
          hr={equipment.hr}
          hrBpm={currentBpm}
          isPaused={isPaused}
          isRunning={isRunning}
          onPlayPause={playPause}
          pauseMenu={{
            stepName: currentNode.label,
            actions: pauseActions,
            onEditSession,
            stopActions,
            collectStopFeedback: true,
          }}
          timing={{
            phaseType,
            phaseLabel,
            timeLeftInPhase: isRepBased ? null : coachState.timeLeftInPhase,
            totalRemaining,
            nextUpLabel,
          }}
        />
      </div>
    </div>
  );

  const equipmentLabel =
    activeDevice?.type === "rower" ? "Rower" : "Bike";

  return (
    <SwipeablePanes
      ref={panesRef}
      className={isImmersive ? "h-[var(--coach-safe-vh)]" : "h-full"}
      indicatorLabels={["Coach", equipmentLabel]}
    >
      {coachPane}
      {equipmentPane}
    </SwipeablePanes>
  );
};

// --- Main Coach View ---
const WorkoutView = () => {
  const {
    appState,
    sessionLoadedIntoCoach,
    workoutPlan,
    startWorkout,
    finishWorkout,
    clearLoadedSession,
  } = useContext(AppStateContext);
  const { user } = useAuth();
  const liveCtx = useLiveSession();

  const liveSnapshotStatus = liveCtx?.liveSession?.snapshot?.status;
  const liveRunning =
    liveSnapshotStatus === "active" || liveSnapshotStatus === "waiting_for_sync";
  const isTimerRunning = appState === "active";
  const [workoutSettings] = useWorkoutSettings();
  // Wake-lock follows the Settings → Audio & Display toggle (no in-coach
  // button). Live followers have no local engine, so the live snapshot
  // status keeps the screen awake during shared sessions too.
  useWakeLock(isTimerRunning || liveRunning, workoutSettings.wakeLockEnabled);
  const deviceCast = useDeviceCast(!!user);

  // Live session (host or guest) — server-authoritative follower flow.
  // Takes precedence over the local engine: once a session is live, all
  // clients render from server snapshots (#28).
  if (liveCtx?.liveSession?.session) {
    return <LiveSessionFlow deviceCast={deviceCast} />;
  }

  // A guided session (LLM-generated or preset-derived) has been loaded
  if (sessionLoadedIntoCoach) {
    return (
      <GuidedSessionView
        appState={appState}
        workoutPlan={workoutPlan}
        onStartWorkout={startWorkout}
        onFinishWorkout={finishWorkout}
        onClearSession={clearLoadedSession}
        deviceCast={deviceCast}
      />
    );
  }

  // Device is running a workout (started on hardware) — show live view
  if (deviceCast.castState === "active" && deviceCast.devices.length > 0) {
    return (
      <div className="grid gap-6">
        <div className="mx-auto w-full max-w-lg">
          <DeviceLiveView
            deviceId={deviceCast.selectedDeviceId || deviceCast.devices[0]?.id}
            onClose={deviceCast.cancelCast}
          />
        </div>
        <CoachAtRest />
      </div>
    );
  }

  // No session loaded — the Coach at rest: ready check, today's suggestion
  // and the timers (presets load into the unified guided engine via
  // loadPresetIntoCoach).
  return <CoachAtRest />;
};

export default WorkoutView;
